/**
 * donation-form — reactive, touched-aware inline error binding for the step
 * views. SYNC: see ../_SYNC.md.
 *
 * Thin Vue layer over the PURE {@link validateDonor} mirror: it computes the live
 * error map from the engine state + document, but only EXPOSES an error for a
 * field once it has been "touched" (blurred) OR the donor has attempted to
 * continue — so errors are not shown noisily on first paint. The
 * pure validator stays the single source of truth; this only governs WHEN a
 * message is visible. Imports `vue` (the views do too); the headless engine
 * never imports it, so engine purity + preview-safety are preserved.
 */
import { computed, reactive, ref } from "vue";
import { validateDonor, } from "./useDonorValidation";
/**
 * Build the touched-aware error binding for a donation context. One instance per
 * `<DonationForm>` is enough (the views share it via the parent), but it is also
 * cheap to create per step view.
 */
export function useFieldErrors(ctx) {
    const touched = reactive(new Set());
    const showAll = ref(false);
    const errors = computed(() => validateDonor(ctx.state, ctx.config.document));
    const touch = (field) => {
        touched.add(field);
    };
    const touchAll = () => {
        showAll.value = true;
    };
    const errorFor = (field) => {
        if (!showAll.value && !touched.has(field)) {
            return undefined;
        }
        return errors.value[field];
    };
    const hasError = (field) => errorFor(field) !== undefined;
    return { errors, touch, touchAll, errorFor, hasError };
}
